/**
 * PolicyModals — the 4-policy switchboard (Ethics · Terms · Privacy · AI Risk)
 * plus the SEO <title>/<meta description> swap side-effect.
 * Extracted byte-verbatim from KnowledgeGraphPage.jsx in SPD v5.53 Phase E.
 *
 * Contract:
 *   - activePolicyModal    : 'ethics' | 'terms' | 'privacy' | 'aiRisk' | null (parent-owned)
 *   - setActivePolicyModal : setter that closes the modal on backdrop/close click
 *   - onOpenRevenueTerms   : optional callback fired from the ethics modal's
 *                            "See terms and Conditions" link. Parent typically
 *                            closes the policy modal, opens the main Menu,
 *                            and switches to the revenue-terms view.
 *
 * The SEO effect swaps the browser tab title + meta description when a
 * policy modal opens, and restores the home defaults on close. Approved
 * title strings per operator, Feb 27 v5.7.
 */
import React, { useEffect } from 'react';

export default function PolicyModals({
  activePolicyModal,
  setActivePolicyModal,
  onOpenRevenueTerms,
}) {
  // SEO — swap <title> + meta description when a policy modal opens, then
  // restore on close. Approved title strings per user, Feb 27 v5.7.
  useEffect(() => {
    const HOME_TITLE = 'Search.Inc – User First AI Agent / Search Engine Hybrid | Google Alternative';
    const HOME_DESC = 'Search.Inc — The First User-First, AI Agent / Search Engine for Canada And The World. A Google Alternative built on user control, data ownership data privacy, AI transparency, and no surveillance. And the only user revenue share where users participate in the value they help create.';
    const POLICY_META = {
      ethics:  { title: 'Ethics Policy — Trustworthy & Ethical AI Search Engine | Search.Inc',
                 desc:  'How Search.Inc behaves when nobody is watching. Ethical AI search built on AI transparency and a refusal to engage in surveillance.' },
      terms:   { title: 'Terms of Service — Search.Inc',
                 desc:  'The contract between you and Search.Inc — including your data-ownership rights.' },
      privacy: { title: 'Privacy Policy — Data Ownership & No-Tracking Search | Search.Inc',
                 desc:  'Search.Inc does not know who you are. Anonymous search, no tracking, no ads. A search engine that doesn\u2019t track you. Full data ownership stays with you.' },
      aiRisk:  { title: 'AI Transparency & Explainability — Search.Inc',
                 desc:  'How Search.Inc handles AI risk. Built on AI transparency, AI explainability, and a commitment to trustworthy AI.' },
    };
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    if (activePolicyModal && POLICY_META[activePolicyModal]) {
      const m = POLICY_META[activePolicyModal];
      document.title = m.title;
      meta.setAttribute('content', m.desc);
    } else {
      document.title = HOME_TITLE;
      meta.setAttribute('content', HOME_DESC);
    }
  }, [activePolicyModal]);

  if (!activePolicyModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="policy-modal-overlay">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setActivePolicyModal(null)} />
      
      <div 
        className="relative z-10 w-full max-w-4xl mx-2 sm:mx-4 rounded-2xl max-h-[85vh] flex flex-col" 
        style={{ backgroundColor: 'rgba(120, 125, 140, 0.92)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}
        data-testid={`policy-modal-${activePolicyModal}`}
      >
        {/* Close button */}
        <button 
          onClick={() => setActivePolicyModal(null)} 
          className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-2xl z-20" 
          data-testid="policy-modal-close"
        >
          &times;
        </button>

        {/* Scrollable content area */}
        <div className="overflow-y-auto p-4 sm:p-10 policy-content">
          
          {/* Ethics Policy */}
          {activePolicyModal === 'ethics' && (
            <div className="text-white">
              <img
                src="/searchinc-logo.png"
                alt="Search.Inc — Actually not evil"
                style={{ height: 60 }}
                className="mb-4 select-none"
                draggable={false}
                data-testid="policy-ethics-brand-logo"
              />
              <h1 className="text-3xl font-bold mb-2 text-cyan-400">Search.Inc Ethics Policy</h1>
              <p className="text-gray-300 text-sm mb-6 italic">An Ethical Search Engine for the People</p>
              
              <p className="mb-6 text-gray-200 leading-relaxed">
                At Search.Inc, we believe that Artificial Intelligence (A.I.) and search should serve people (the individual) first and allow for fair competition for businesses. We don't care about your politics, your race, gender or whatever. Everyone is equal and everyone should get equal access to the web. Our mission is to be the first User-First AI Agent / Search Engine for Canada and the World: accurate, transparent, neutral, and built on user control, data ownership, data privacy, AI transparency, and no surveillance.
              </p>
              <p className="mb-6 text-gray-200 leading-relaxed">
                Unlike other platforms that prioritize their own profits above all else through ad revenue and selling your data, we are committed to fairness, accuracy, and freedom of information without compromise. We're proudly Canadian owned and Canadian built.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Compassionate Capitalism</h2>
              <p className="mb-4 text-gray-200 leading-relaxed">
                The search industry has been dominated by corporations that see users as products and search results as revenue streams. Search.Inc is different. We want you, the subscriber, user and loyal customer to share in our revenue. As we grow our market share and increase our revenue so you too can share in that.
              </p>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>For just $35 CAD per year $2.10 CAD per month, you can get a share of almost $1 Billion CAD* at 4% market share (<button type="button" onClick={() => { if (onOpenRevenueTerms) onOpenRevenueTerms(); }} className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors" data-testid="ethics-revenue-share-link">See terms and Conditions</button>)</li>
                <li>No stocks, no Capital Raising you'll never have access to</li>
                <li>Just use our product and earn access to our revenue</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Privacy is Non-Negotiable</h2>
              <p className="mb-4 text-gray-200 leading-relaxed">
                For subscribers, who you are as an individual and who you are as a username are two separate things that we, nor anyone else can ever connect to be one and the same person. That's why we can't sell your data or give it to anyone who requests it.
              </p>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>Your data belongs to you, and you control it</li>
                <li>Search.Inc does not track, store, or sell user data unless you tell us we can</li>
                <li>You can turn on, off, or delete your username data at any time</li>
                <li>You can "go nuclear" and delete everything related to your username</li>
                <li>You have "the right to be forgotten"</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">True Search Neutrality and Bias Mitigation</h2>
              <p className="mb-4 text-gray-200 leading-relaxed">
                The results you get are based on our proprietary AI Intent algorithms that look to only unpack your intent.
              </p>
              <ul className="list-disc mb-6 space-y-2 text-gray-200 ml-8">
                <li>Search results reflect quality and relevance, not financial incentives</li>
                <li>No one can pay to influence search rankings</li>
                <li>We do not manipulate results for political or commercial gain</li>
                <li>Users can customize their search preferences without being nudged toward a single viewpoint</li>
                <li>We will be introducing ads at a later date. This will be a new type of experience. It's important to note that Ads will have no connection or influence search results</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">A Level of User Control Over Returned Results Algorithm</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                Search engines shouldn't be black boxes. Search.Inc provides clear, public-facing explanations of how our ranking system works. Users will be able fine-tune how results are presented, ensuring that AI-driven recommendations serve their needs, not corporate interests.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Empowering Users, Not Controlling Them</h2>
              <p className="mb-4 text-gray-200 leading-relaxed">We believe in customization without compromise. With Search.Inc, you can:</p>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>Through your Control Centre manage almost everything to do with username data</li>
                <li>Use returned results functions to see the web the way you want without hidden censorship</li>
                <li>If you provide your search history, we can provide more accurate unbiased information</li>
                <li>If you choose not to permit us to use your data, you will still get accurate results</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Individuals, Families and Children</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                This is important. Other Search Companies will analyze, sell and use you and your children's data for profit building and psychological, political and societal manipulation. Giving you control of any data connected to you, your family or those you care about is the best way to help stop this.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">A Stand Against Misinformation</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                Unlike some competitors, we do not arbitrarily suppress information or selectively down-rank content based on politics or corporate interests. Instead, we provide clear, user-visible labels on sources, allowing you to judge credibility. Our focus is on fact-based ranking, not editorial manipulation.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">What We Will Never Do</h2>
              <p className="mb-4 text-gray-200 leading-relaxed">At Search.Inc, we actively reject:</p>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li><strong>The sale of user data</strong> — We don't track you, so there's nothing to sell</li>
                <li><strong>Opaque censorship rules</strong> — Clear definitions of what is or is not censored</li>
                <li><strong>Military or surveillance AI partnerships</strong> — Our technology is for public good</li>
                <li><strong>Facial recognition</strong> — This is creepy</li>
                <li><strong>Recording without consent</strong> — No listening through devices</li>
                <li><strong>Ad-driven ranking manipulation</strong> — Results are determined by relevance</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Sustainability</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                We build and do everything as efficiently as possible. Companies and governments are the biggest contributors to environmental problems. We want to do our part by being as sustainable as possible.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">We Won't Always Get It Right</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                We won't, but if we get something wrong, we'll own up to it and do our best to fix it. Common sense and our guiding principles are the guide rails to how we will approach everything.
              </p>

              <p className="mt-8 text-cyan-400 font-semibold text-lg">Search.Inc — *Actually* Not Evil.</p>
            </div>
          )}

          {/* Terms of Service */}
          {activePolicyModal === 'terms' && (
            <div className="text-white">
              <h1 className="text-3xl font-bold mb-2 text-cyan-400">Terms of Service</h1>
              <p className="text-gray-300 text-sm mb-6 italic">Effective Date: Coming Soon</p>
              
              <div className="bg-gray-700/40 rounded-xl p-8 text-center">
                <h2 className="text-xl font-semibold mb-4 text-gray-200">Terms of Service Coming Soon</h2>
                <p className="text-gray-300 leading-relaxed">
                  Our full Terms of Service document is currently being finalized. Please check back soon for the complete terms governing your use of Search.Inc services.
                </p>
                <p className="text-gray-400 mt-4 text-sm">
                  In the meantime, please refer to our Ethics Policy, Privacy Policy, and AI & Data Risk Policy for information about how we operate.
                </p>
              </div>
            </div>
          )}

          {/* Privacy Policy */}
          {activePolicyModal === 'privacy' && (
            <div className="text-white">
              <img
                src="/searchinc-logo.png"
                alt="Search.Inc — Actually not evil"
                style={{ height: 60 }}
                className="mb-4 select-none"
                draggable={false}
                data-testid="policy-privacy-brand-logo"
              />
              <h1 className="text-3xl font-bold mb-2 text-cyan-400">Search.Inc Privacy Policy</h1>
              <p className="text-gray-300 text-sm mb-6 italic">Our Privacy Policy for the People — We Don't Know Who You Are</p>
              
              <p className="mb-6 text-gray-200 leading-relaxed">
                We don't care who you are in the real world. Your username is yours to invent and yours alone. We cannot and will not link it back to your real-life identity. Not even if someone asks nicely. Not even if they wave a <strong className="text-cyan-300">billion dollars</strong> in our faces. Not even if we're ordered to.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Data Belongs to You. Not Us.</h2>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>You control what's collected</li>
                <li>You control how it's used</li>
                <li>You can turn off tracking at any time</li>
                <li>You can delete your entire data profile with a single click (we call it "going nuclear")</li>
                <li>You can view what your data's doing in real-time from your Control Centre</li>
                <li>We don't mine it, we only keep it for up to 60 days (NOT forever), and we don't sell it</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">No Tracking Means No Profiling Means No Creepy Ads</h2>
              <p className="mb-4 text-gray-200 leading-relaxed">
                You have the power. That's the most important aspect. You can authorize giving us access to your username data, location, social and media rich data.
              </p>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>We do not track your browsing</li>
                <li>We do not profile your behaviour to create a creepy digital twin of you</li>
                <li>We do not serve you ads based on private thoughts you've never shared</li>
                <li>If we do serve ads, they're contextual—not personal</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">What Little Data We Do Collect (and Why)</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                If you opt-in (again: your choice), we might collect limited behavioural data to improve your search experience and accuracy. That's it. You can view, manage, and delete everything from your Control Centre.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Family & Kids: Off-Limits</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                We will <strong className="text-red-400">never</strong> analyze, sell, or exploit children's data. Period. No fingerprinting. No voice recording. No license plate scanning. No excuses.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Data Deletion = Actual Deletion</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                If you hit delete, it's gone. Not archived, not moved to cold storage. <strong className="text-cyan-300">Gone-gone.</strong> Your search history, profile info, opt-in preferences—everything.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">We Don't Play Well With…</h2>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>Data brokers</li>
                <li>Ad tech giants</li>
                <li>Government agencies asking for bulk surveillance access</li>
                <li>Creepy startups building facial recognition tools</li>
                <li>Military-grade AI firms</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Encryption as Standard</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                If you do choose to store anything with us (like preferences or subscriptions), it's encrypted at rest, in flight, and every other way we can lock it down. We use bank-grade, hacker-repelling security and update it regularly.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">You Have Rights. We Make Them Real.</h2>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>The right to be forgotten</li>
                <li>The right to opt out</li>
                <li>The right to transparency</li>
                <li>The right to delete everything</li>
                <li>The right to see how your data is being used (if you've opted in)</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Our Pledge to You</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                If someone tries to water this down or sneak in 'minor updates', we want you to raise hell. You are the safeguard. We've built mechanisms to make it hard to violate this policy without a digital rebellion.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">Final Word: You Are Not the Product</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                You are the customer. The human. The purpose. This isn't a trick. It's the future of ethical search. And it starts with respecting your privacy like it's non-negotiable—<strong className="text-cyan-300">because it is.</strong>
              </p>
            </div>
          )}

          {/* AI & Data Risk Policy */}
          {activePolicyModal === 'aiRisk' && (
            <div className="text-white">
              <img
                src="/searchinc-logo.png"
                alt="Search.Inc — Actually not evil"
                style={{ height: 60 }}
                className="mb-4 select-none"
                draggable={false}
                data-testid="policy-ai-risk-brand-logo"
              />
              <h1 className="text-3xl font-bold mb-2 text-cyan-400">Search.Inc Ethical Global AI Data and Risk Governance Policy</h1>
              <p className="text-gray-300 text-sm mb-6 italic">Responsible and ethical AI development, deployment, and governance</p>
              
              <h2 className="text-xl font-bold mt-6 mb-4 text-cyan-300">1. Introduction</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                Search.Inc is committed to the responsible and ethical development, deployment, and governance of AI within our search engine platform. Our AI-driven search engine adheres to strict principles that prioritize user privacy, transparency, fairness, and accountability. This policy outlines our approach to identifying, assessing, and mitigating risks associated with AI technologies to ensure ethical and safe use for all users.
              </p>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">2. Guiding Principles</h2>
              <p className="mb-4 text-gray-200">Our AI and Data governance is based on the following ethical principles:</p>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li><strong className="text-cyan-200">Actually Not Evil:</strong> Help humanity flourish whilst not allowing hate, discrimination or criminal behaviour</li>
                <li><strong className="text-cyan-200">Fair Competition:</strong> Strengthen and provide innovative capabilities to increase competition</li>
                <li><strong className="text-cyan-200">Control and Consent:</strong> Users own and control their data. Period.</li>
                <li><strong className="text-cyan-200">Egalitarian and Freedom of Expression:</strong> Everyone is equal, bias is continuously managed</li>
                <li><strong className="text-cyan-200">Safety and Security:</strong> Paramount and embedded in every aspect of our design</li>
                <li><strong className="text-cyan-200">Universal Access:</strong> Equal access, accessibility and language for everyone</li>
                <li><strong className="text-cyan-200">Continuous Innovation:</strong> Incentivised to protect data and provide new AI protocols</li>
                <li><strong className="text-cyan-200">Transparency & Portability:</strong> Full visibility on how we manage and use AI & data</li>
                <li><strong className="text-cyan-200">Sustainability:</strong> Search.Inc is designed to be carbon neutral from the ground up</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">2.1 Transparency</h3>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li>We clearly disclose when AI is used to generate search results or recommendations</li>
                <li>Users have access to explanations of how AI-driven results are ranked and refined</li>
                <li>Our AI models and data usage policies are documented and periodically reviewed</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">2.2 Privacy & Data Protection</h3>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li>User search queries, history, and interactions will not be used for unauthorized profiling</li>
                <li>Users have full control and stewardship over their data</li>
                <li>Strong encryption and secure data storage measures prevent breaches and misuse</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">2.3 Fairness & Non-Discrimination</h3>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li>AI systems are trained on diverse, representative, and unbiased datasets</li>
                <li>Regular audits assess AI outcomes for fairness and inclusivity</li>
                <li>Users can challenge and report biased or harmful AI-generated results</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">2.4 Accountability & Human Oversight</h3>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li>AI decision-making remains accountable to human oversight</li>
                <li>Reinforcement Learning from Human Feedback (RLHF) continually improves results</li>
                <li>A designated Ethics & AI Risk Governance team oversees AI model updates</li>
                <li>Clear reporting channels for users to flag ethical concerns</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">2.5 Safety & Security</h3>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>AI systems undergo continuous security assessments</li>
                <li>AI-driven results are evaluated to prevent harmful content and misinformation</li>
                <li>Robust safeguards against AI-generated disinformation and malicious content</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">3. AI Risk Assessment & Mitigation</h2>
              <p className="mb-4 text-gray-200">We employ a structured AI risk assessment framework to identify and address ethical, security, and societal risks.</p>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">3.1 Risk Identification</h3>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li>Conduct pre-deployment risk assessments for AI models and new features</li>
                <li>Regularly monitor AI performance for unintended consequences</li>
                <li>Engage external experts and ethics committees for independent risk reviews</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">3.2 Risk Categorization</h3>
              <p className="mb-2 text-gray-200">We classify AI risks based on their impact and likelihood:</p>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li><strong className="text-green-400">Low-risk:</strong> Minor impact on user experience, easily correctable</li>
                <li><strong className="text-yellow-400">Medium-risk:</strong> Potential for bias, misinformation, or reduced transparency</li>
                <li><strong className="text-red-400">High-risk:</strong> Significant impact on privacy, discrimination, or security</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">3.3 Risk Mitigation Strategies</h3>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>Implement algorithmic audits and bias detection tools</li>
                <li>Develop clear user feedback mechanisms to refine AI decision-making</li>
                <li>Utilize human-in-the-loop approaches for high-risk decision areas</li>
                <li>Establish red-teaming process to test vulnerabilities in AI-driven search algorithms</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">4. User Rights & Controls</h2>
              
              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">4.1 AI Explainability & Opt-Out</h3>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li>Users have the right to understand how AI influences their search results</li>
                <li>Users can opt out of AI-driven personalization</li>
                <li>Users can delete their data at any time, making it irretrievable</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">4.2 Appeals & Redress</h3>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>Users can appeal AI-driven search results and request manual review</li>
                <li>A dedicated user support channel handles AI-related complaints</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">5. Compliance</h2>
              
              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">5.1 Regulatory Compliance</h3>
              <p className="mb-4 text-gray-200">Our AI governance framework aligns with global AI regulations, including:</p>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li>EU AI Act</li>
                <li>GDPR</li>
                <li>AI ethics guidelines from OECD, UNESCO</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">5.2 Ongoing Monitoring & Audits</h3>
              <ul className="list-disc list-inside mb-4 space-y-2 text-gray-200 ml-4">
                <li>Regular internal and external audits of AI systems</li>
                <li>AI policies revisited at least annually</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-cyan-200">5.3 Stakeholder Engagement</h3>
              <ul className="list-disc list-inside mb-6 space-y-2 text-gray-200 ml-4">
                <li>Active engagement with policymakers, civil society, and academia</li>
                <li>Open forums and transparency reports keep users informed</li>
              </ul>

              <h2 className="text-xl font-bold mt-8 mb-4 text-cyan-300">6. Conclusion</h2>
              <p className="mb-6 text-gray-200 leading-relaxed">
                This Ethical AI & Data Risk Governance Policy serves as a foundation for ensuring responsible AI use within Search.Inc. We are committed to upholding transparency, fairness, privacy, and accountability while continuously improving our AI systems to better serve users in an ethical and secure manner.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
